import {IElement, IArtboardZIndexChain, ICommandInformationBK, ITextAttributes, ITextSelectionInformation, IImageAttributes, ISelectDescribeData, IElementType, ITextStyle, ITextPartAttributes, ICommonAttributes, IShapeCommonAttributes} from '@routes/private/Editor/types';
import {IClipBoardElement} from '@routes/private/Editor/types';
import { cloneDeep } from 'lodash';
import Konva from 'konva';
import C from '@assets/themes/colors';
import { getStyleValue, getSelectionStyleValue, intersec, isTextPartCommand } from 'utils/textMultiStyling'
import { IDSConfig } from '@state/dsconfig/types';

const uuid = require('react-uuid');

const padding_pickers_X = 283;
const padding_select_describe_X = 40;
const padding_pickers_Y = 70;
const padding_select_describe_Y = 150;

const attributesDefault : any= {
  opacity: 1,
  rotation: 0,
  textAlign: "left",
  flip: "00",
  clipping: false,
  textBaseline: "alphabetic",
  direction: 'ltr',   
  letterSpacing: undefined,
  lineHeight: 1.2,
  borderColor: undefined,
  borderWidth: undefined,
  borderDash: undefined,
  fontWeight: false,
  fontUnderline: false,
  fontSize: 12
}

export type ILimits = {
  left: number, 
  top: number, 
  right: number, 
  bottom: number
}

export const findElementById = ( data: IElement[], id: string): IElement | undefined => {
  let result = undefined;
  data.forEach(element => {
    if (element.id === id) result = element;
    else if (element.children && element.children.length > 0) {
      let child = findElementById(element.children, id );
      if (child) result = child;
    } 
  });
  return result;
}

export const findElementsByIds = ( data: IElement[], ids: string[]): (IElement|undefined)[] => {
  let elements = ids.map((id)=>{ 
    const element = findElementById(data, id);
    if (element) return element!;
  });
  return elements;
}

const elementHasAttribute = (element: IElement, attribute: string): boolean => {
  if (element.attributes.hasOwnProperty(attribute)) return true; 
  if (element.type==="Shape" && attribute === "backgroundColor" && (element.attributes as IShapeCommonAttributes).bgGradient?.colorStops?.length) return true;
  if (element.type==="Text" && (element.attributes as ITextAttributes).style.hasOwnProperty(attribute)) return true;
  if (element.type==="Text" && (element.attributes as ITextAttributes).parts) {
    for (let i=0;i<(element.attributes as ITextAttributes).parts.length;i++) {
      if ((element.attributes as ITextAttributes).parts[i].style && (element.attributes as ITextAttributes).parts[i].style.hasOwnProperty(attribute)) return true;
    }
  }
  return false;
}

const getElementAttributeValue = (element: IElement, attribute: string, textSelectionInformation?: ITextSelectionInformation) => {
  let value: any = undefined;
  if (element.attributes.hasOwnProperty(attribute)) {
    value = element.attributes[attribute];
  }
  else if (element.type && element.type==="Text") {
    if (textSelectionInformation) {
      value = getSelectionStyleValue(element.attributes as ITextAttributes, attribute, textSelectionInformation);
    }
    else {
      value = getStyleValue((element.attributes as ITextAttributes).style,attribute);
    }
  }
  return value;
}

const getAllTextGeneralAndPartsDifferentValues = (element: IElement, attribute: string, textSelectionInformation?: ITextSelectionInformation): any[] => {
  let values: any[] = [];
  const valueGeneral = getStyleValue((element.attributes as ITextAttributes).style, attribute);
  if (!textSelectionInformation && valueGeneral) values.push(valueGeneral);
  let totalCount = 0;
  for (let i=0;i<(element.attributes as ITextAttributes).parts.length;i++) {
    const valuePart = getStyleValue((element.attributes as ITextAttributes).parts[i].style, attribute);
    if (!textSelectionInformation) {
      if (valuePart) values.push(valuePart);
    }
    else {
      const selectionStart = textSelectionInformation.start;
      let selectionEnd = textSelectionInformation.start + textSelectionInformation.length -1;
      if (selectionEnd < selectionStart) selectionEnd = selectionStart;
      const partStart = totalCount;
      const partEnd = totalCount + (element.attributes as ITextAttributes).parts[i].length -1;
      const inPart = intersec(selectionStart, selectionEnd, partStart, partEnd);
      if (inPart) {
        if (!valuePart && valueGeneral) values.push(valueGeneral);
        if (valuePart) values.push(valuePart);
      }
    }
    totalCount += (element.attributes as ITextAttributes).parts[i].length;
  }
  return values;  
}

const getAllElementDifferentAttributeValues = (element: IElement, attribute: string, textSelectionInformation?: ITextSelectionInformation): any[] => {
  let values: any[] = [];
  if (element.type === "Shape" && attribute === "backgroundColor") {
    const bgGradient = (element.attributes as IShapeCommonAttributes).bgGradient;
    if (bgGradient && ['linear', 'radial'].includes(bgGradient?.style ?? '')) {
      bgGradient.colorStops?.map(colorStop => {
        if (colorStop.color) values.push(colorStop.color);
      });
    } else if (element.attributes.backgroundColor) {
      values.push(element.attributes.backgroundColor);
    }
  } else if (element.attributes.hasOwnProperty(attribute)) {
    values.push(element.attributes[attribute]);
  }
  else if (element.type && element.type==="Text") {
      values = getAllTextGeneralAndPartsDifferentValues(element, attribute, textSelectionInformation);
  }
  return values;
}

export const findCommonAttributeValue = ( elementIds: string[], data: IElement[], attribute: string, elementType?: string, textSelectionInformation?: ITextSelectionInformation): (string|undefined) => {
  let value = undefined;
  let elementTypesWithAttribute: string[] = [];
  for(let i=0;i<elementIds.length;i++) {
    let elementSelected = findElementById(data, elementIds[i]);
    if (elementSelected && elementHasAttribute(elementSelected, attribute) && (elementType===undefined || elementSelected.type===elementType)) {
      if (!elementTypesWithAttribute.includes(elementSelected.type)){
        elementTypesWithAttribute.push(elementSelected.type);
      }
      if (value === undefined) value = getElementAttributeValue(elementSelected, attribute, textSelectionInformation);
      else if (value !== getElementAttributeValue(elementSelected, attribute, textSelectionInformation)) return undefined;
    }
  }
  for(let i=0;i<elementIds.length;i++) {
    let elementSelected = findElementById(data, elementIds[i]);
    if (elementSelected && elementTypesWithAttribute.includes(elementSelected.type) && !(elementHasAttribute(elementSelected, attribute))) {
      return undefined;
    }
  }
  return value;
}

export const findDifferentAttributeValues = ( elementIds: string[], data: IElement[], attribute: string, elementType?: string, textSelectionInformation?: ITextSelectionInformation): (string[]) => {
  let values : string[] = [];
  for(let i=0;i<elementIds.length;i++) {
    let elementSelected = findElementById(data, elementIds[i]);
    if (elementSelected && elementHasAttribute(elementSelected, attribute) && (elementType===undefined || elementSelected.type===elementType)) {
      const diferentValues = getAllElementDifferentAttributeValues(elementSelected, attribute, textSelectionInformation);
      for (let a=0;a<diferentValues.length;a++) {
        if (diferentValues[a]!==undefined && !values.includes(diferentValues[a])) {
          values.push(diferentValues[a]);
        }
      }
    }
  }
  return values;
}

export const findArtboard = ( data: IElement[], id: string): IElement | undefined => {
  let result: IElement | undefined = undefined;
  data.forEach(element => {
    if (element.type === "Artboard" && element.children && element.children.length > 0) {
      let child = findElementById(element.children, id );
      if (child) result = element;
    }
  });
  return result;
}

export const findArtboardIndex = ( data: IElement[], id: string): number | undefined => {
  let result: number | undefined = undefined;
  for (let i=0;i<data.length;i++) {
    const element = data[i];
    if (element.type === "Artboard" && element.children && element.children.length > 0) {
      let child = findElementById(element.children, id );
      if (child) result = i;
    }
  }
  return result;
}

export const getBoundaries = (stage: Konva.Stage | null, artboard: IElement, childrenElements: IElement[]) => {
  let elements: IElement[] = [];
  childrenElements.map(childrenElement => {
    const item = getRelativeClientRect(stage, `${artboard.id}-object`, childrenElement.id);
    if (item) {
      delete item.node;
      const element = {attributes: {...item}} as IElement;
      elements = [...elements, element];
    }
  });
  let boundaries: any = {left: undefined, top: undefined, right: undefined, bottom: undefined};
  if (elements.length === 1) {
    boundaries.left = artboard.attributes.x;
    boundaries.top = artboard.attributes.y;
    boundaries.right = artboard.attributes.x + artboard.attributes.width;
    boundaries.bottom = artboard.attributes.y + artboard.attributes.height;
  } else if (elements.length > 1) {
    elements.map(element => {
      const elementLeft = element.attributes.x;
      const elementTop = element.attributes.y;
      const elementRight = element.attributes.x + element.attributes.width;
      const elementBottom = element.attributes.y + element.attributes.height;
      boundaries.left = boundaries.left && boundaries.left < elementLeft ? boundaries.left : elementLeft;
      boundaries.top = boundaries.top && boundaries.top < elementTop ? boundaries.top : elementTop;
      boundaries.right = boundaries.right && boundaries.right > elementRight ? boundaries.right : elementRight;
      boundaries.bottom = boundaries.bottom && boundaries.bottom > elementBottom ? boundaries.bottom : elementBottom;
    })
  }
  return boundaries;
}

export const findStageItem = (stage: Konva.Stage | null, name: string, id: string): Konva.Node | undefined => {
  let item: Konva.Node | undefined = undefined;
  stage?.find(`.${name}`).map((nodeItem) => {if (nodeItem.id() === id && nodeItem.visible()) item = nodeItem; return});
  return item;
}

export const getRelativeClientRect = (stage: Konva.Stage | null, name: string, id: string, bringNode?: boolean) => {
  const item = findStageItem(stage, name, id);
  const node = bringNode ? item : undefined;
  if (item) return {...item.getClientRect({relativeTo: stage as any}), node};
  else return undefined;
}

export const updateElements = (data: IElement[], updatedElements: IElement[]):IElement[] => {
  for (let i=0;i<updatedElements.length;i++) {
    data = updateElement(data, updatedElements[i]);
  }
  return data;
}

export const updateElement = (data: IElement[], updatedElement: IElement):IElement[] => {
  return [...data].map(element => {
    if (element.id === updatedElement.id) return {...updatedElement};
    else if (element.children && element.children.length > 0) {
      return {...element, children: updateElement(element.children, updatedElement)};
    } 
    else return {...element};
  });
}

export const findOEM = (entityInformation: any): string | undefined => {
  if(entityInformation && entityInformation.tags && entityInformation.tags.oem) {
      return entityInformation.tags.oem;
  }
  return undefined;
}

export const sameElementType = (ids: string[], elementType: string, data: IElement[]): boolean => {
    let elements: IElement[] = [];
    ids.forEach(id => {
      const element = findElementById(data, id);
      if (element) elements.push(element);
    });
    return elements.every((element) => element.type === elementType);
}

export const sameElementsTypes = (ids: string[], data: IElement[]): boolean => {
  let finalIds: string[] = [];
  ids.forEach(id => {
    const element = findElementById(data, id);
    if (element) finalIds.push(id);
  });
  if(finalIds && finalIds.length>0) {
    const elementType: string = findElementById(data, finalIds[0])!.type; 
    return sameElementType(finalIds, elementType, data);
  }
  return false;
}

export const hasAtLeastOneElementType = (ids: string[], data: IElement[], elementType: IElementType): boolean => {
  let elements: IElement[] = [];
  ids.forEach(id => {
    const element = findElementById(data, id);
    if (element) elements.push(element);
  });
  for(let i=0;i<elements.length;i++){
    if(elements[i].type==elementType) return true;
  }
  return false;
}

export const isLockedElement = (ids: string[], data: IElement[]): boolean => {
  let isLocked: boolean =  false;
  ids.forEach(id => {
    const element = findElementById(data, id);
    if (!["Artboards" , "Artboard" , "Group"].includes(element?.type??'') && element?.attributes.locked) isLocked =  true;
  });
  return isLocked;
}

export const prepareElementForClipBoard = (element: IElement, data: IElement[]): IClipBoardElement => {
  let artboard: IElement|undefined = undefined;
  if (data[0].children) artboard = findArtboard(data[0].children, element.id);
  if (!artboard) artboard = data[0];
  let elementForClipBoard: IClipBoardElement = {element: cloneDeep(element), originalElement: element, parentId: getParentElementId(element.id, data)!, artboardId: artboard.id};
  elementForClipBoard.element.id = uuid();
  elementForClipBoard.element.name = getNameForClipBoard(elementForClipBoard.element.name, data);
  elementForClipBoard.element.children = undefined;
  if (elementForClipBoard.element.attributes && elementForClipBoard.element.attributes.x) 
    elementForClipBoard.element.attributes.x = elementForClipBoard.element.attributes.x + 10;
  if (elementForClipBoard.element.attributes && elementForClipBoard.element.attributes.y) 
    elementForClipBoard.element.attributes.y = elementForClipBoard.element.attributes.y + 10;
  if (elementForClipBoard.element.attributes.rawProps?.length && elementForClipBoard.element.attributes.rawProps[0]?.mask) {
    if (elementForClipBoard.element.attributes.rawProps[0].mask.left) elementForClipBoard.element.attributes.rawProps[0].mask.left = elementForClipBoard.element.attributes.rawProps[0].mask.left+10;       
    if (elementForClipBoard.element.attributes.rawProps[0].mask.top) elementForClipBoard.element.attributes.rawProps[0].mask.top = elementForClipBoard.element.attributes.rawProps[0].mask.top+10;       
    if (elementForClipBoard.element.attributes.rawProps[0].mask.right) elementForClipBoard.element.attributes.rawProps[0].mask.right = elementForClipBoard.element.attributes.rawProps[0].mask.right+10;       
    if (elementForClipBoard.element.attributes.rawProps[0].mask.bottom) elementForClipBoard.element.attributes.rawProps[0].mask.bottom = elementForClipBoard.element.attributes.rawProps[0].mask.bottom+10;       
  }
  if (element.type==="Image") {
    let imageName = element.id;
    if ((element.attributes as IImageAttributes).srcImage){
      imageName = (element.attributes as IImageAttributes).srcImage!;
    }
    (elementForClipBoard.element.attributes as IImageAttributes).srcImage = imageName;
    (elementForClipBoard.element.attributes as IImageAttributes).image.x = (elementForClipBoard.element.attributes as IImageAttributes).image.x + 10;
    (elementForClipBoard.element.attributes as IImageAttributes).image.y = (elementForClipBoard.element.attributes as IImageAttributes).image.y + 10;
  }
  return elementForClipBoard;
}

export const prepareSetForClipBoard = (element: IElement, setName: string, data: IElement[]): IClipBoardElement => {
  let elementForClipBoard: IClipBoardElement = {element: cloneDeep(element), originalElement: element, parentId: getParentElementId(element.id, data)!, artboardId: undefined, setName: setName};
  elementForClipBoard.element.id = uuid();  
  elementForClipBoard.element.name = setName;
  if (elementForClipBoard.element.children)
    elementForClipBoard.element.children = prepareArtboardChildForClipBoard(elementForClipBoard.element.children, 0 , 0);  
  return elementForClipBoard;
}

export const prepareArtboardForClipBoard = (element: IElement, data: IElement[], dsConfig: IDSConfig): IClipBoardElement => {
  let pos = getPosNewArtboard(data[0].children!, dsConfig);  
  let elementForClipBoard: IClipBoardElement = {element: cloneDeep(element), originalElement: element, parentId: getParentElementId(element.id, data)!, artboardId: undefined};
  elementForClipBoard.element.id = uuid();  
  elementForClipBoard.element.name = getNameForClipBoard(elementForClipBoard.element.name, data);  

  if (elementForClipBoard.element.attributes.y == 0)
    elementForClipBoard.element.attributes.y = pos.coorY;

  if (elementForClipBoard.element.children)
    elementForClipBoard.element.children = prepareArtboardChildForClipBoard(elementForClipBoard.element.children,(pos.coorX - element.attributes.x),(pos.coorY - element.attributes.y));  
  if (elementForClipBoard.element.attributes && elementForClipBoard.element.attributes.x) 
    elementForClipBoard.element.attributes.x = pos.coorX;
  if (elementForClipBoard.element.attributes && elementForClipBoard.element.attributes.y) 
    elementForClipBoard.element.attributes.y = pos.coorY;
  
  return elementForClipBoard;
}

export const prepareArtboardChildForClipBoard = (childs: IElement[], posX:number, posY:number): IElement[] => {    
  childs.map((child: IElement) => {
      child.id = uuid();
      
      if (child.attributes && child.attributes.x) 
        child.attributes.x = child.attributes.x + posX;
      if (child.attributes && child.attributes.y) 
        child.attributes.y = child.attributes.y + posY;
      if (child.children)
        child.children = prepareArtboardChildForClipBoard(child.children,posX,posY);          
      if (child.attributes.rawProps?.length && child.attributes.rawProps[0]?.mask) {
        if (child.attributes.rawProps[0].mask.left) child.attributes.rawProps[0].mask.left = child.attributes.rawProps[0].mask.left+posX;       
        if (child.attributes.rawProps[0].mask.top) child.attributes.rawProps[0].mask.top = child.attributes.rawProps[0].mask.top+posY;       
        if (child.attributes.rawProps[0].mask.right) child.attributes.rawProps[0].mask.right = child.attributes.rawProps[0].mask.right+posX;       
        if (child.attributes.rawProps[0].mask.bottom) child.attributes.rawProps[0].mask.bottom = child.attributes.rawProps[0].mask.bottom+posY;       
      }
      if (child.type==="Image") {
        let imageName = child.id;
        if ((child.attributes as IImageAttributes).srcImage){
          imageName = (child.attributes as IImageAttributes).srcImage!;
        }
        (child.attributes as IImageAttributes).srcImage = imageName;
        (child.attributes as IImageAttributes).image.x = (child.attributes as IImageAttributes).image.x + posX;
        (child.attributes as IImageAttributes).image.y = (child.attributes as IImageAttributes).image.y + posY;
      }
  })    
  return childs;
}


export const prepareClipBoardForReCopy = (elements: IClipBoardElement[], data: IElement[], dsConfig:any): IClipBoardElement[] => {
  let newElements = cloneDeep(elements);

  newElements.map((clip:IClipBoardElement)=>{
    clip.element.id = uuid();
    if (clip.element.type == "Artboard")    
    {
      clip.element.name = getNameForClipBoard(clip.element.name, data);      
      let pos = getPosNewArtboard(data[0].children!, dsConfig);      
      if (clip.element.attributes && clip.element.attributes.x) 
        clip.element.attributes.x = pos.coorX;
      if (clip.element.attributes && clip.element.attributes.y) 
        clip.element.attributes.y = pos.coorY + (clip.element.attributes.height + pos.space);
      if (clip.element.children)
        clip.element.children = prepareArtboardChildForClipBoard(clip.element.children,(pos.coorX- clip.element.attributes.x),((clip.element.attributes.height + pos.space)));
    }
    else
    {
      clip.element.name = getNameForClipBoard(clip.element.name, data);      
      if (clip.element.attributes && clip.element.attributes.x) 
        clip.element.attributes.x = clip.element.attributes.x + 10;
      if (clip.element.attributes && clip.element.attributes.y) 
        clip.element.attributes.y = clip.element.attributes.y + 10;
      if (clip.element.attributes.rawProps?.length && clip.element.attributes.rawProps[0]?.mask) {
        if (clip.element.attributes.rawProps[0].mask.left) clip.element.attributes.rawProps[0].mask.left = clip.element.attributes.rawProps[0].mask.left+10;       
        if (clip.element.attributes.rawProps[0].mask.top) clip.element.attributes.rawProps[0].mask.top = clip.element.attributes.rawProps[0].mask.top+10;       
        if (clip.element.attributes.rawProps[0].mask.right) clip.element.attributes.rawProps[0].mask.right = clip.element.attributes.rawProps[0].mask.right+10;       
        if (clip.element.attributes.rawProps[0].mask.bottom) clip.element.attributes.rawProps[0].mask.bottom = clip.element.attributes.rawProps[0].mask.bottom+10;       
      }
      if (clip.element.attributes && clip.element.attributes.image) {
        (clip.element.attributes as IImageAttributes).image.x = (clip.element.attributes as IImageAttributes).image.x + 10;
        (clip.element.attributes as IImageAttributes).image.y = (clip.element.attributes as IImageAttributes).image.y + 10;
      }
    }
  })


  return newElements;
}

export const getNameForClipBoard = (elementName: string, data: IElement[]): string => {
  let name = elementName;
  if (name.indexOf(" copy") === -1) {
    name += " copy";
  }
  else {
    name = name.substr(0, name.indexOf(" copy") + 5);
  }
  if (!existsElementName(elementName, data)) {
    return name;
  }
  else {
    let secuence: number = 2;
    if (name.indexOf(" copy ")){
    }
    while(existsElementName(name + " " + secuence.toString(), data)) {
      secuence++;
    }
    return name + " " + secuence.toString();
  }
}

export const existsElementName = (elementName: string, data: IElement[]): boolean => {
  for(let i=0;i<data.length;i++) {
    if(data[i].name === elementName) {
      return true;
    }
    if(data[i].children && existsElementName(elementName, data[i].children!)) {
      return true;
    }
  }
  return false;
}

export const getParentElement = (elementId: string, data: IElement[]): IElement|undefined => {
  for(let i=0;i<data.length;i++) {
    if (data[i].children && data[i].children!.length>0) {
      for(let a=0;a<data[i].children!.length;a++) {
        if (data[i].children![a] && data[i].children![a].id === elementId) {
          return data[i];
        }
      }
      if (data[i].children!) {
        let element: IElement|undefined = getParentElement(elementId, data[i].children!);
        if (element) return element;
      }
    }
  }
  return undefined;
}

export const getParentElementId = (elementId: string, data: IElement[]): string|undefined => {
  const element = getParentElement(elementId, data);
  if (element) {
    return element.id;
  }
  return undefined;
}

export const addElementToParent = (parentId: string, element: IElement, data: IElement[]): IElement[] => {
  let newData = data;
  for(let i=0;i<newData.length;i++) {
    if (newData[i].id === parentId) {
      if (!newData[i].children) {
        newData[i].children = [];
      }
      newData[i].children?.push(element);
    }
    if (newData[i].children && newData[i].children!.length>0) {
        newData[i].children = addElementToParent(parentId, element, newData[i].children!);
    }
  }
  return newData;
}

export const getUndoRedoCommandBK = (element: IElement, command: any, type: string, value?: any, selectDescribe?: ISelectDescribeData, artboardsZIndexChain?: IArtboardZIndexChain[]): ICommandInformationBK => {
  let undoCommand: ICommandInformationBK = {idElement: element.id, type: "COMMAND_OPTION", command: command, value: "" };
  if ((command==="addElement" && type==="UNDO") || (command==="removeElement" && type==="REDO")){ 
    undoCommand.command = "removeElement";
    undoCommand.type = "REMOVE_ELEMENT";
    undoCommand.value = value;
  } else if ((command==="removeElement" && type==="UNDO") || (command==="addElement" && type==="REDO")) {
    undoCommand.command = "addElement";
    undoCommand.type = "ADD_ELEMENT";
    undoCommand.value = value;
  } else if (command==="copypaste") {
    undoCommand.command = "copypaste";
    undoCommand.type = "COPY_PASTE";
    undoCommand.value = value;
  } else if (command==="srcImage") {
    undoCommand.command = "srcImage";
    undoCommand.type = "COMMAND_OPTION";
    undoCommand.value = value ?? {srcImage: element.attributes.srcImage ?? element.id, width: element.attributes.width, height: element.attributes.height, brandId: element.attributes.brandId, brandImageType: element.attributes.brandImageType};
  } else if (command==="resize") {
    undoCommand.command = "resize";
    undoCommand.type = "RESIZE_ELEMENT";
    undoCommand.value = {
      rotation: element.attributes.rotation ? element.attributes.rotation: 0,           
      x: element.attributes.x,
      y: element.attributes.y,
      width: element.attributes.width,
      height: element.attributes.height
    };
    if (element.type == "Image") {
      undoCommand.value = {
        rotation: element.attributes.rotation ? element.attributes.rotation: 0,           
        x: element.attributes.x,
        y: element.attributes.y,
        width: element.attributes.width,
        height: element.attributes.height,
        image: {
          x: element.attributes.image.x,
          y: element.attributes.image.y,
          width: element.attributes.image.width,
          height: element.attributes.image.height
        }
      };
    }
  } else if (command==="changePositionsChain") {
    undoCommand.command = "changePositionsChain";
    undoCommand.type = "POSITION";
    undoCommand.value = (element.attributes as any).zIndexChain 
                        ?(element.attributes as any).zIndexChain
                        : []; 
  } else if (command==="hideUnhide") {
    undoCommand.command = "hideUnhide";
    undoCommand.type = "COMMAND_OPTION";
    undoCommand.value = ((element.attributes as any)["hidden"]??false).toString(); 
  } else if (isTextPartCommand(command)) {
    undoCommand.command = "updateTextElement";
    undoCommand.type = "TEXT_COMMAND_OPTION";
    undoCommand.value = element.attributes; 
  } else {
    undoCommand.type = "COMMAND_OPTION";
    undoCommand.value = (element.attributes as any)[command] 
                        ?(element.attributes as any)[command]
                        : attributesDefault[command];
  }
  if (selectDescribe) {
    undoCommand.selectDescribe = selectDescribe;
  }
  if (artboardsZIndexChain) { 
    undoCommand.artboardsZIndexChain = cloneDeep(artboardsZIndexChain);
  }
  return undoCommand;
}

export const stageZoom = (stageRef: any, event: any, zoom: any) => {
  if (stageRef.current !== null) {
    const stage = stageRef.current;
    const oldScale = stage.scaleX();

    let { x: pointerX, y: pointerY } = stage.getPointerPosition();
    let mousePointTo = {
      x: (pointerX - stage.x()) / oldScale,
      y: (pointerY - stage.y()) / oldScale,
    };

    if (zoom.point==="center") {
      pointerX = stage.width() / 2;
      pointerY = stage.height() / 2
    
      mousePointTo = {
        x: (pointerX - stage.x()) / oldScale,
        y: (pointerY - stage.y()) / oldScale,
      };
    }
    
    const newScale = event ? event.evt.deltaY > 0 ? oldScale * zoom.value : oldScale / zoom.value : zoom.value/100;

    stage.scale({ x: newScale, y: newScale });
    const newPos = {
      x: pointerX - mousePointTo.x * newScale,
      y: pointerY - mousePointTo.y * newScale,
    }

    stage.position(newPos);
    stage.batchDraw();
  }
}

export const getRect = (artboards: IElement[]) => {
  let rect: {x: number | null, y: number | null, x2: number | null, y2: number | null, width: number, height: number} = {
    x: null,
    y: null,
    x2: null,
    y2: null,
    width: 0,
    height: 0
  }
  artboards.map((artboard:IElement) => {
    if (rect.x===null || artboard.attributes.x < rect.x) rect.x=artboard.attributes.x;
    if (rect.y===null || artboard.attributes.y < rect.y) rect.y=artboard.attributes.y;

    if (rect.x2===null || artboard.attributes.x+artboard.attributes?.width > rect.x2) rect.x2=artboard.attributes.x+artboard.attributes.width;
    if (rect.y2===null || artboard.attributes.y+artboard.attributes?.height > rect.y2) rect.y2=artboard.attributes.y+artboard.attributes.height;
  });

  if (rect.x!==null && rect.x2!==null) rect.width = rect.x2-rect.x;
  if (rect.y!==null && rect.y2!==null) rect.height = rect.y2-rect.y;

  return rect;
}

export const centerStage = (stage: any, artboards:IElement[], selectDescribe: boolean, onZoomChange: any) => {
  let box =getRect(artboards);
  let paddingX = selectDescribe ? padding_select_describe_X : padding_pickers_X;
  let paddingY = selectDescribe ? padding_select_describe_Y : padding_pickers_Y;    

  const scale = Math.min(
    stage.width() / (box.width + paddingX*2),
    stage.height() / (box.height + paddingY*2)
  );

  let calcXpadding = paddingX + ((stage.width()-box.width)*scale)/2;
  if (selectDescribe) calcXpadding = paddingX;

  stage.setAttrs({
    scaleX: scale,
    scaleY: scale
  });

  onZoomChange(scale*100);
  //stage.draw();
  stage.batchDraw();

  let layer = stage.children[0];

  let rect: {x: number | null, y: number | null, x2: number | null, y2: number | null, width: number, height: number} = {
    x: null,
    y: null,
    x2: null,
    y2: null,
    width: 0,
    height: 0
  }
  layer.children.map((groupElementNode:any) => {
    if (groupElementNode.attrs.type === "artboard") {
      const elBox = groupElementNode.children[0].getClientRect();
      if (rect.x===null || elBox.x < rect.x) rect.x=elBox.x;
      if (rect.y===null || elBox.y < rect.y) rect.y=elBox.y;

      if (rect.x2===null || elBox.x+elBox?.width > rect.x2) rect.x2=elBox.x+elBox.width;
      if (rect.y2===null || elBox.y+elBox?.height > rect.y2) rect.y2=elBox.y+elBox.height;




    }
  });
  if (rect.x!==null && rect.x2!==null) rect.width = rect.x2-rect.x;
  if (rect.y!==null && rect.y2!==null) rect.height = rect.y2-rect.y;

  let calcX = (stage.width()-rect.width)/2;
  if (calcX<paddingX) calcX = paddingX;

  let calcY = (stage.height()-rect.height)/2;
  if (calcY<paddingY) calcY = paddingY;  

  stage.setAttrs({
    y: rect.y ? calcY+rect.y *-1 : calcY,
    x: rect.x ? calcX+rect.x *-1 : calcX,
  });
}

export const refreshStage = (stage: any, artboards:IElement[], selectDescribe: boolean, onZoomChange: any) => {
   
  stage.setAttrs({        
    y: 0,
    x: 0,
  });  
  //stage.draw(); 
  stage.batchDraw(); 
  centerStage(stage,artboards,selectDescribe,onZoomChange);
}

export const reverseScale = (nodeRef: any, positionY?: number) => {
  let node = nodeRef.current;
  const absScale = node.getAbsoluteScale();
  let scaleY = node.scaleY() / absScale.y;
  node.scaleX(node.scaleX() / absScale.x);
  node.scaleY(scaleY);
  if (positionY) node.y(positionY - (node.height()+8)*scaleY);
}

export const moveStage = (stageRef: any, event:any) => {
  const stage = stageRef.current;

  const newPos = {
    x: stage.x() - event.evt.deltaX,
    y: stage.y() - event.evt.deltaY,
  }

  stage.position(newPos);
  stage.batchDraw()
}

export const moveStageXY = (stageRef: any, moveX: number, moveY: number) => {
  if (stageRef.current) { 
    const stage = stageRef.current;
    const stageScale = stage.scaleX();
    moveX = moveX*stageScale;
    moveY = moveY*stageScale;

    const newPos = {
      x: stage.x() - moveX,
      y: stage.y() - moveY,
    }

    stage.position(newPos);
    stage.batchDraw();
  }
}

export const moveDropAreas = (layer: Konva.Layer | null, setDropAreas: any) => {
  let newDropAreas : any[] = [];

  layer?.getChildren().forEach((groupElementNode: any) => {
    let parentID =  groupElementNode.attrs.id
    if (groupElementNode.attrs.type === "artboard" && groupElementNode.children[0]) {
      let node =groupElementNode.children[0];
        let rect = node.getClientRect();
        let newDrop: any = {id: parentID, attributes: {}};
        newDrop.attributes.x = rect.x;
        newDrop.attributes.y = rect.y;
        newDrop.attributes.width = rect.width;
        newDrop.attributes.height = rect.height;
        newDropAreas.push(newDrop);
    };
  });
  
  setDropAreas(newDropAreas);
}

export const insideArtboard = (layerRef: any, elementRef: any, containerId: any) => {
  let artboardBox: any | undefined = undefined;
  layerRef.current.getChildren().forEach((groupElementNode: any) => {
    if (groupElementNode.attrs.id===containerId && groupElementNode.attrs.type === "artboard") artboardBox = groupElementNode.children[0].getClientRect();
  });
  const elementBox = elementRef.current ? elementRef.current.getClientRect() : elementRef.getClientRect();
  return Konva.Util.haveIntersection(artboardBox, elementBox);
}

export const dropInArtboard = (layerRef: any, elementRef: any, containerId?: string) => {
  let artboard : any| undefined = undefined;
  const elementBox = elementRef.current ? elementRef.current.getClientRect() : elementRef.getClientRect();
  layerRef.current.getChildren().forEach((groupElementNode: any) => {
    if (groupElementNode.attrs.type === "artboard") {
      let artboardBox = groupElementNode.children[0].getClientRect();
      if (Konva.Util.haveIntersection(artboardBox, elementBox) && (artboard?.attrs.id!==containerId || containerId===undefined)) 
        artboard = groupElementNode;
    }
  });
  return artboard;
}

export const moveElement = (e:any, element:any, deltas: any) => {
  let node = element.node;

  let delta = (e.shiftKey || e.metaKey) ? deltas.fast : deltas.normal;
  let x = 0, y = 0;
  
  if (e.key === "ArrowLeft") x= x - delta;
  if (e.key === "ArrowRight") x= x + delta;
  if (e.key === "ArrowUp") y= y - delta;
  if (e.key === "ArrowDown") y= y + delta;

  
  node.setPosition({x: node.x() + x , y: node.y() + y});
  node.fire("dragmove");
}

export const getOEMName = (brandId: string, dsConfig: any): string => {
  const brandName: string = dsConfig?.brands.find((oem: any) => { return oem.id === brandId})?.name ?? brandId;
  return brandName;
}

export const getBrandIdByOEMName = (oemName: string, dsConfig: any): string => {
  const brandId: string = dsConfig?.brands.find((oem: any) => { return oem.name === oemName})?.id ?? oemName;
  return brandId;
}


export const getPosNewArtboard=(data: IElement[], dsConfig: IDSConfig)=>{
  
  let posX:any=null;
  let posY:any=null;

  let artboard: any = undefined;
  let heightSpaces: number[] = [];
  //Find to lowest position
  if (data.length > 0)
  {        
      data.map((child: IElement) => {

        let childAttributes=  (child.attributes) as ICommonAttributes
        if (posX==null && posY == null)
        {
          posX = childAttributes.x;
          posY = childAttributes.y + childAttributes.height;
        }
        else
        {
          if (childAttributes.x< posX)
            posX = childAttributes.x;
          if ((childAttributes.y + childAttributes.height )> posY)
            posY = childAttributes.y + childAttributes.height;
        }   
        
        heightSpaces = [...heightSpaces,...getHeightSpace(child,data!)];        
      });
  }
  let defaultSpace = 50;
  if (dsConfig)
  {
    if  (dsConfig.editorConfig.artboardResize)
      defaultSpace = dsConfig.editorConfig.artboardResize.minSpaceY??50;
  }
  
  if (heightSpaces.length>0)
    defaultSpace = Math.min(...heightSpaces)
  
  return {coorX:posX, coorY:posY + defaultSpace, space:defaultSpace};
}

const getHeightSpace = (artboard:IElement, childs:IElement[]): number[] =>
{
  let artboardAttributes=  (artboard.attributes) as ICommonAttributes
  let heights:number[]=[]
  childs.map((child: IElement) => {
    let childAttributes=  (child.attributes) as ICommonAttributes
    if (artboard.id!= child.id){
      if (childAttributes.y > (artboardAttributes.y + artboardAttributes.height))            
        heights.push(childAttributes.y - (artboardAttributes.y + artboardAttributes.height))
      else if (artboardAttributes.y > (childAttributes.y + childAttributes.height))
        heights.push(artboardAttributes.y - (childAttributes.y+ childAttributes.height))    
    }
  })
  return heights;
}